import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export interface DealerPackage {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  features?: string[];
  maxListings?: number;
  isActive: boolean;
  sortOrder: number;
}

export interface DealerRegistrationData {
  // Step tracking
  step?: number;

  // Step 1: Company Info
  companyName: string;
  street: string;
  streetNumber: string;
  additionalAddress?: string;
  zipCode: string;
  city: string;
  phone: string;
  email: string;

  // Step 2: Package Selection
  dealerPackageId?: string;

  // Step 3: Legal & Credentials
  termsAccepted?: boolean;
  privacyAccepted?: boolean;
  businessRegistration?: boolean;
  registrationCode?: string;
  imprint?: string;
  dealerEmail?: string;
  dealerPassword?: string;
}

export interface DealerResponse {
  dealerId: string;
  status: 'pending' | 'approved' | 'rejected';
  paymentStatus: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  data: DealerRegistrationData;
}

export interface CreateDealerResponse {
  dealerId: string;
  message: string;
  data: DealerRegistrationData;
}

export interface DealerPaymentResponse {
  paymentId: string;
  amount: number;
  packageName: string;
  paymentUrl?: string | null;
  formFields?: Record<string, string> | null;
  upayUrl?: string | null;
}

export interface DealerCashPaymentResponse {
  paymentId: string;
  paymentCode: string;
  amount: number;
  packageName: string;
  instructions: string;
}

export interface DealerSubscription {
  id: string;
  dealerId: string;
  dealerPackageId: string;
  paymentId?: string | null;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
  dealerPackage?: DealerPackage;
}

export interface DealerInvoice {
  id: string;
  invoiceNumber: string;
  dealerId: string;
  paymentId: string;
  dealerSubscriptionId?: string | null;
  amount: number;
  currency: string;
  description: string;
  status: 'PENDING' | 'PAID' | 'CANCELLED';
  items?: any;
  issuedAt: string;
  paidAt?: string | null;
  dealerSubscription?: DealerSubscription & { dealerPackage?: DealerPackage };
}

export interface SubscribeResponse {
  paymentId: string;
  amount: number;
  packageName: string;
  paymentUrl?: string | null;
  paymentCode?: string | null;
  formFields?: Record<string, string> | null;
  upayUrl?: string | null;
  instructions?: string | null;
  free?: boolean;
}

export const dealerService = {
  // Get active dealer packages (for registration form)
  getDealerPackages: async (): Promise<DealerPackage[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DEALER.PACKAGES);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  },

  // Create initial dealer registration (step 1)
  createDealerRegistration: async (
    data: Partial<DealerRegistrationData>
  ): Promise<CreateDealerResponse> => {
    const response = await axiosInstance.post<CreateDealerResponse>(
      API_ENDPOINTS.DEALER.CREATE,
      data
    );
    return response.data;
  },

  // Get dealer data by ID
  getDealerData: async (dealerId: string): Promise<DealerResponse> => {
    const response = await axiosInstance.get<DealerResponse>(
      API_ENDPOINTS.DEALER.GET_BY_ID(dealerId)
    );
    return response.data;
  },

  // Update dealer registration (PATCH for partial updates)
  updateDealerRegistration: async (
    dealerId: string,
    data: Partial<DealerRegistrationData>
  ): Promise<DealerResponse> => {
    const response = await axiosInstance.patch<DealerResponse>(
      API_ENDPOINTS.DEALER.PATCH(dealerId),
      data
    );
    return response.data;
  },

  // Submit final dealer registration
  submitDealerRegistration: async (
    dealerId: string
  ): Promise<DealerResponse> => {
    const response = await axiosInstance.post<DealerResponse>(
      API_ENDPOINTS.DEALER.SUBMIT(dealerId)
    );
    return response.data;
  },

  // Check dealer status
  getDealerStatus: async (
    dealerId: string
  ): Promise<{
    status: 'pending' | 'approved' | 'rejected';
    paymentStatus: 'pending' | 'approved' | 'rejected';
    canAddListings: boolean;
  }> => {
    const response = await axiosInstance.get(
      API_ENDPOINTS.DEALER.STATUS(dealerId)
    );
    return response.data;
  },

  // Get dealer's listings
  getDealerListings: async (dealerId: string): Promise<any[]> => {
    const response = await axiosInstance.get(
      API_ENDPOINTS.DEALER.LISTINGS(dealerId)
    );
    return response.data;
  },

  // Initiate UPay card payment for dealer subscription
  initiateDealerCardPayment: async (
    dealerId: string
  ): Promise<DealerPaymentResponse> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.PAYMENTS.INITIATE_DEALER,
      { dealerId }
    );
    return response.data;
  },

  // Create UPay cash/QR payment for dealer subscription
  initiateDealerCashPayment: async (
    dealerId: string
  ): Promise<DealerCashPaymentResponse> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.PAYMENTS.PURCHASE_DEALER_CASH,
      { dealerId }
    );
    return response.data;
  },

  // Subscribe to dealer package (card or cash)
  subscribeDealerPackage: async (dealerId: string, paymentMethod: 'CARD' | 'CASH', packageId?: string): Promise<SubscribeResponse> => {
    const response = await axiosInstance.post(API_ENDPOINTS.PAYMENTS.SUBSCRIBE_DEALER, { dealerId, paymentMethod, ...(packageId ? { packageId } : {}) });
    return response.data;
  },

  // Get public dealer profile (shop page)
  getDealerPublicProfile: async (dealerId: string) => {
    const response = await axiosInstance.get(API_ENDPOINTS.DEALER.PUBLIC_PROFILE(dealerId));
    return response.data as {
      id: string;
      companyName: string;
      city: string;
      country: string;
      phone: string;
      email: string;
      memberSince: string;
      totalListings: number;
      listings: {
        id: string; slug: string | null; title: string; make: string; model: string;
        year: number; price: number | null; mileage: number | null;
        fuelType: string | null; transmission: string | null;
        city: string | null; image: string | null; createdAt: string;
      }[];
    };
  },

  // Get active dealer subscription
  getActiveSubscription: async (dealerId: string): Promise<DealerSubscription | null> => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.DEALER.SUBSCRIPTION(dealerId));
      return response.data;
    } catch {
      return null;
    }
  },

  // Get dealer invoices
  getDealerInvoices: async (dealerId: string): Promise<DealerInvoice[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DEALER.INVOICES(dealerId));
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  },

  // ADMIN: All dealers
  getAllDealers: async (): Promise<any[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.DEALER.ALL_DEALERS);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  },

  // ADMIN: Dealer approval queue
  getDealerApprovalQueue: async (): Promise<any[]> => {
    const response = await axiosInstance.get(
      API_ENDPOINTS.DEALER.APPROVAL_QUEUE
    );
    return response.data;
  },

  // ADMIN: Approve dealer
  approveDealer: async (
    dealerId: string,
    body?: { adminNotes?: string }
  ): Promise<any> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.DEALER.APPROVE_DEALER(dealerId),
      body || {}
    );
    return response.data;
  },

  // ADMIN: Reject dealer
  rejectDealer: async (
    dealerId: string,
    body: { rejectionReason: string; adminNotes?: string }
  ): Promise<any> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.DEALER.REJECT_DEALER(dealerId),
      body
    );
    return response.data;
  },

  // ADMIN: Dealer payment approval queue
  getDealerPaymentQueue: async (): Promise<any[]> => {
    const response = await axiosInstance.get(
      API_ENDPOINTS.DEALER.PAYMENT_APPROVAL_QUEUE
    );
    return response.data;
  },

  // ADMIN: Approve dealer payment
  approveDealerPayment: async (
    dealerId: string,
    body?: { adminNotes?: string }
  ): Promise<any> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.DEALER.APPROVE_PAYMENT(dealerId),
      body || {}
    );
    return response.data;
  },

  // ADMIN: Reject dealer payment
  rejectDealerPayment: async (
    dealerId: string,
    body: { rejectionReason: string; adminNotes?: string }
  ): Promise<any> => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.DEALER.REJECT_PAYMENT(dealerId),
      body
    );
    return response.data;
  },

  // ADMIN: Create dealer
  adminCreateDealer: async (body: {
    companyName: string;
    email: string;
    phone: string;
    city: string;
    street: string;
    streetNumber: string;
    zipCode: string;
    additionalAddress?: string;
    country?: string;
    dealerEmail?: string;
    dealerPassword?: string;
    dealerPackageId?: string;
    status?: string;
  }): Promise<any> => {
    const response = await axiosInstance.post(API_ENDPOINTS.DEALER.ADMIN_CREATE_DEALER, body);
    return response.data;
  },

  // ADMIN: Update dealer
  adminUpdateDealer: async (
    dealerId: string,
    body: {
      companyName?: string;
      email?: string;
      phone?: string;
      city?: string;
      street?: string;
      streetNumber?: string;
      zipCode?: string;
      additionalAddress?: string;
      country?: string;
      dealerEmail?: string;
      dealerPassword?: string;
      dealerPackageId?: string;
      status?: string;
      adminNotes?: string;
    }
  ): Promise<any> => {
    const response = await axiosInstance.patch(
      API_ENDPOINTS.DEALER.ADMIN_UPDATE_DEALER(dealerId),
      body
    );
    return response.data;
  },
};
